import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import {
  API_MANAGE_LOCATIONS_LIST, // GET /vX/locations
  API_COMPANY_LIST,            // GET /vX/users
  API_BULK_LOCATION_ADD,     // <-- add in service/url.js: `${API_BASE}/locations/bulk-add`
} from "../../../service/url";
import { toast } from 'react-toastify';

export default function BulkLocationAdd() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [locations, setLocations] = useState([]);   // [{location_id, location_name}]
  const [companyList, setCompanyList] = useState([]);           // [{id, username, ...}]

  // UI state
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [locRes, companyRes] = await Promise.all([
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_COMPANY_LIST, {}),
        ]);
        setLocations(Array.isArray(locRes) ? locRes : []);
        setCompanyList(Array.isArray(companyRes) ? companyRes : []);
      } catch (err) {
        toast.error("Failed to load Dialer locations/company");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // search within company list
  const filteredCompany = useMemo(
    () =>
      (companyList || []).filter((u) =>
        (u.name || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [companyList, searchValue]
  );

  const clearSearch = () => {
    setSearchValue("");
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = filteredCompany.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedCompanyIds.includes(id));

    if (allSelected) {
      setSelectedCompanyIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBulkAdd = async () => {
    if (!selectedLocationId) {
      toast.error("Please select a Dialer location first.");
      return;
    }
    if (selectedCompanyIds.length === 0) {
      toast.error("Select at least one Company.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        company_ids: selectedCompanyIds,
        location_id: selectedLocationId, // <-- ensure backend expects `location_id`
      };
      const res = await postApiCall(API_BULK_LOCATION_ADD, payload);

      if (res) {
        toast.success(res.message || "Company added to Dialer location successfully");
        setSelectedCompanyIds([]);
        // keep selectedLocationId so you can continue adding into same location
      } else {
        toast.error(res?.message || "Failed to add Company");
      }
    } catch (err) {
      toast.error(err?.message || "Error adding Company to Dialer location");
    } finally {
      setSubmitting(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Row style={{marginTop:"20px"}}>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk Company Dialer Location Add</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Controls Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Dropdown
                  value={rows}
                  options={[
                    { label: "5", value: 5 },
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                  ]}
                  onChange={(e) => setRows(e.value)}
                  style={{ width: 90, height: 40, display: "flex", alignItems: "center" }}
                />
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button label="Clear" className="p-button-secondary" onClick={clearSearch} />

                {/* Location selector */}
                <Dropdown
                  value={selectedLocationId}
                  options={(locations || []).map((l) => ({
                    label: l.location_name,
                    value: l.location_id,
                  }))}
                  onChange={(e) => setSelectedLocationId(e.value)}
                  placeholder="Select Dialer Location"
                  style={{ width: 260, marginLeft: 10 }}
                />

                {/* Submit */}
                <Button
                style={{background:"#3A57E8", border:"#3A57E8"}}
                  label="Add to Dialer Location"
                  className="p-button-success"
                  disabled={selectedCompanyIds.length === 0 || !selectedLocationId || submitting}
                  onClick={() => setConfirmVisible(true)}
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm ml-2"
                      role="status"
                      aria-hidden="true"
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </Button>
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={filteredCompany}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No Company found"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          filteredCompany.length > 0 &&
                          filteredCompany.every((u) => selectedCompanyIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedCompanyIds.includes(row.id)}
                        onChange={() => toggleCompany(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="Company Id" sortable />
                  <Column field="name" header="Company Name" sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Add"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button label="Confirm" className="p-button-success" onClick={handleBulkAdd} disabled={submitting} />
          </div>
        }
      >
        <p>Are you sure you want to add {selectedCompanyIds.length} Company(s) to this Dialer location?</p>
      </Dialog>
    </div>
  );
}
